/**
 * MembershipChoicePanel — three-path picker shown to non-members during
 * event registration.
 *
 * Triggered when /registration-policy returns is_member=false AND
 * member_fee_enabled=true. Lets the contestant pick:
 *
 *   1. "Pay the non-member fee" — accept the per-event surcharge for this
 *      rodeo. Most importantly: makes clear they LOSE card-owner draw
 *      priority. The preference resolver enforces this on the backend.
 *
 *   2. "I'm already a member" — inline save flow that creates a pending
 *      organization_memberships row for the user. After save the policy
 *      flips to is_member=true (the row counts as a member for lookups)
 *      so this entire panel disappears and Pay Later/Cash unlock; admins
 *      can later approve or reject the flagged claim.
 *
 *   3. "Become a member" — opens the org's external signup URL in a new
 *      tab (rendered as a secondary action below the radiogroup, not as
 *      a selectable in-flow path).
 *
 * UX guarantees:
 *   - Each in-flow choice is a clearly selectable card in a true
 *     `role="radiogroup"` (native radio inputs + arrow-key nav).
 *   - The member-number input is rendered up front — disabled until the
 *     "I'm already a member" card is selected — so users can see the
 *     entry affordance without clicking around.
 *   - Continue is gated by useRegistrationForm validation: no selection,
 *     or claim_member without a saved number, blocks step navigation.
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  AlertCircle,
  BadgeCheck,
  Check,
  DollarSign,
  ExternalLink,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useClaimMembership } from '@/api/extensions/useMembershipVerificationOps';
import { useToast } from '@/contexts/ToastContext';

export type NonMemberChoice = 'pay_non_member' | 'claim_member' | null;

interface MembershipChoicePanelProps {
  surcharge: number;
  membershipUrl: string | null;
  allowBuddyGroup: boolean;
  /**
   * Org the registration is for. Required to create the
   * organization_memberships row when the user saves their member number
   * inline. Null disables the "I'm already a member" save flow (rare edge
   * case — orgless rodeos hide this whole panel anyway).
   */
  organizationId: string | null;
  choice: NonMemberChoice;
  claimedMemberNumber: string;
  onChoiceChange: (choice: NonMemberChoice) => void;
  onMemberNumberChange: (value: string) => void;
}

export function MembershipChoicePanel({
  surcharge,
  membershipUrl,
  allowBuddyGroup,
  organizationId,
  choice,
  claimedMemberNumber,
  onChoiceChange,
  onMemberNumberChange,
}: MembershipChoicePanelProps) {
  const radiogroupLabelId = useId();
  const payOptionId = useId();
  const memberOptionId = useId();
  const memberNumberInputId = useId();
  const memberInputRef = useRef<HTMLInputElement>(null);

  const claimMembership = useClaimMembership();
  const { showSuccess, showError } = useToast();
  const [saveError, setSaveError] = useState<string | null>(null);

  const trimmedMemberNumber = claimedMemberNumber.trim();
  const isClaimSelected = choice === 'claim_member';
  const isPaySelected = choice === 'pay_non_member';
  const canSaveMemberNumber =
    !!organizationId && trimmedMemberNumber.length > 0 && !claimMembership.isPending;
  const canBecomeMember = !!(membershipUrl && membershipUrl.trim().length > 0);
  // When the rodeo has no non-member surcharge configured, the "pay the
  // fee" framing is misleading — there's nothing to pay. We keep the radio
  // option in place (validation still requires a choice, and the draw-
  // priority / buddy-group consequences still apply) but relabel it as a
  // plain "enter as a non-member" acknowledgement.
  const hasSurcharge = surcharge > 0;

  // Auto-focus the member-number input when the user picks the "I'm
  // already a member" card so they can start typing without an extra
  // click. We focus only on the rising edge (select transition) — not on
  // every render — otherwise typing into other inputs in the modal would
  // steal focus.
  useEffect(() => {
    if (isClaimSelected) {
      // Defer to next tick so the input has finished its disabled→enabled
      // transition; browsers ignore focus() calls on still-disabled inputs.
      const id = window.setTimeout(() => {
        memberInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [isClaimSelected]);

  const handleSaveMemberNumber = async () => {
    if (!organizationId) {
      setSaveError('No organization linked to this rodeo.');
      return;
    }
    if (trimmedMemberNumber.length === 0) {
      setSaveError('Enter your member number first.');
      return;
    }
    setSaveError(null);
    try {
      await claimMembership.mutateAsync({
        organizationId,
        memberNumber: trimmedMemberNumber,
      });
      showSuccess(
        'Member number saved',
        "You're flagged as a member. An admin will verify it shortly.",
      );
      // The mutation invalidates registration-ops; the parent modal will
      // re-fetch the policy and this whole panel will unmount as is_member
      // flips to true. No local state cleanup needed.
    } catch (err: any) {
      const msg =
        err?.body?.error ??
        err?.message ??
        'Could not save your member number. Please try again.';
      setSaveError(msg);
      showError('Could not save member number', msg);
    }
  };

  // Shared classes for the two selectable cards. We use ring + border for
  // a strong "this is selectable, this is selected" signal at-a-glance —
  // a flat border alone reads as a label, which is exactly the bug this
  // refactor fixes.
  const cardBase =
    'group relative block w-full text-left rounded-lg border-2 p-4 transition-all cursor-pointer ' +
    'focus-within:ring-2 focus-within:ring-primary/40 focus-within:ring-offset-1 focus-within:ring-offset-background';
  const cardUnselected =
    'border-border bg-card hover:border-primary/60 hover:bg-primary/[0.03] hover:shadow-sm';
  const cardSelected =
    'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-sm';

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-4">
      <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p id={radiogroupLabelId} className="font-medium">
            You aren't on this organization's member list — pick how you'd like to enter.
          </p>
          <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-0.5">
            Tap one of the cards below. You must select an option to continue.
          </p>
        </div>
      </div>

      {/* Selectable cards live inside a true radiogroup. Native radios are
          hidden visually but kept in the tab order so keyboard arrow-key
          navigation between options works for free, and screen readers
          announce "radio, X of Y, checked/not checked" correctly. */}
      <div
        role="radiogroup"
        aria-labelledby={radiogroupLabelId}
        aria-required="true"
        className="space-y-3"
      >
        {/* Option 1 — pay non-member fee */}
        <label
          htmlFor={payOptionId}
          className={`${cardBase} ${isPaySelected ? cardSelected : cardUnselected}`}
        >
          <input
            id={payOptionId}
            type="radio"
            name="non-member-choice"
            value="pay_non_member"
            checked={isPaySelected}
            onChange={() => onChoiceChange('pay_non_member')}
            className="sr-only"
          />
          <div className="flex items-start gap-3">
            {/* Custom radio dot — bigger and more obvious than the native
                control, which we hide for layout reasons (still focusable). */}
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                isPaySelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/40 bg-background group-hover:border-primary/60'
              }`}
            >
              {isPaySelected && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="whitespace-nowrap">
                  {hasSurcharge ? 'Pay the non-member fee' : 'Enter as a non-member'}
                </span>
                {hasSurcharge && (
                  <span className="ml-auto whitespace-nowrap font-semibold text-base">
                    {formatCurrency(surcharge)}
                  </span>
                )}
              </div>
              <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
                {hasSurcharge && (
                  <li>Charged once per event entry on this rodeo.</li>
                )}
                {!hasSurcharge && (
                  <li>
                    This rodeo doesn't charge a non-member fee, so there's
                    nothing extra to pay.
                  </li>
                )}
                <li>
                  <strong className="text-amber-700 dark:text-amber-300">
                    You lose card-owner draw priority
                  </strong>{' '}
                  — confirmed members are drawn ahead of you.
                </li>
                {!allowBuddyGroup && (
                  <li>You won't be able to join a buddy group at this org.</li>
                )}
              </ul>
            </div>
          </div>
        </label>

        {/* Option 2 — I'm already a member. The member-number input is
            always rendered so users can see they CAN enter their number;
            it stays disabled until this card is selected. Selecting the
            card enables + auto-focuses the input (see effect above). */}
        <label
          htmlFor={memberOptionId}
          className={`${cardBase} ${isClaimSelected ? cardSelected : cardUnselected}`}
        >
          <input
            id={memberOptionId}
            type="radio"
            name="non-member-choice"
            value="claim_member"
            checked={isClaimSelected}
            onChange={() => onChoiceChange('claim_member')}
            className="sr-only"
          />
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                isClaimSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/40 bg-background group-hover:border-primary/60'
              }`}
            >
              {isClaimSelected && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <BadgeCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="whitespace-nowrap">I'm already a member</span>
                {!isClaimSelected && (
                  <span className="ml-auto inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    Tap to enter
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Save your member number to unlock member pricing, draw priority,
                and the Pay Later / Cash payment options. An admin will verify
                it — it stays active in the meantime.
              </p>

              {/* Disabled-when-unselected member-number entry. Stops
                  propagation so clicking the input doesn't toggle the
                  radio off (label click + clicking-on-input both reach
                  the label). Keep the click handler explicit to make sure
                  clicking the disabled wrapper still flips the radio on. */}
              <div
                className="mt-3 space-y-2"
                onClick={(e) => {
                  // The wrapper is inside the <label>, so a click here
                  // already activates the radio via label semantics. We
                  // only need to swallow the bubble for the Save button
                  // (handled inline below) and prevent the input itself
                  // from re-toggling.
                  if (!isClaimSelected) {
                    onChoiceChange('claim_member');
                  }
                  e.stopPropagation();
                }}
              >
                <Label
                  htmlFor={memberNumberInputId}
                  className={`text-xs ${
                    isClaimSelected ? '' : 'text-muted-foreground/70'
                  }`}
                >
                  Member / card number
                </Label>
                <div className="flex items-stretch gap-2">
                  <Input
                    ref={memberInputRef}
                    id={memberNumberInputId}
                    type="text"
                    autoComplete="off"
                    placeholder={
                      isClaimSelected ? 'e.g. 12345' : 'Enter member number'
                    }
                    value={claimedMemberNumber}
                    onChange={(e) => {
                      onMemberNumberChange(e.target.value);
                      if (saveError) setSaveError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canSaveMemberNumber) {
                        e.preventDefault();
                        handleSaveMemberNumber();
                      }
                    }}
                    onClick={(e) => {
                      // Only swallow the bubble once the card is active —
                      // otherwise tapping the disabled input should still
                      // promote this card to selected (handled by the
                      // wrapper's onClick above).
                      if (isClaimSelected) e.stopPropagation();
                    }}
                    className={`h-9 text-sm flex-1 ${
                      isClaimSelected ? '' : 'bg-muted/40'
                    }`}
                    disabled={!isClaimSelected || claimMembership.isPending}
                    aria-disabled={!isClaimSelected}
                    // Keep the disabled input out of the tab order so
                    // keyboard users don't tab onto a dead control.
                    tabIndex={isClaimSelected ? 0 : -1}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveMemberNumber();
                    }}
                    disabled={!isClaimSelected || !canSaveMemberNumber}
                    className="h-9 shrink-0"
                  >
                    {claimMembership.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Save member number'
                    )}
                  </Button>
                </div>
                {!isClaimSelected && (
                  <p className="text-[11px] text-muted-foreground italic">
                    Tap this card to enable the member-number field.
                  </p>
                )}
                {saveError && isClaimSelected && (
                  <p className="text-xs text-destructive">{saveError}</p>
                )}
                {isClaimSelected && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1">
                    <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>
                      You'll be treated as a member right away. If an admin can't
                      confirm your number later they'll reject the claim and the
                      non-member fee will be added on to any current or future
                      registrations.
                    </span>
                  </p>
                )}
                {isClaimSelected && !organizationId && (
                  <p className="text-xs text-muted-foreground italic">
                    This rodeo isn't linked to an organization, so member numbers
                    can't be saved here.
                  </p>
                )}
              </div>
            </div>
          </div>
        </label>
      </div>

      {/* Secondary action — "Become a member" is an external link out to
          the org's signup page, not an in-flow registration path. We keep
          it visually separate (and de-emphasized) from the radiogroup so
          users don't confuse it with the two selectable options above. */}
      <div className="pt-1">
        <button
          type="button"
          disabled={!canBecomeMember}
          onClick={() => {
            if (!canBecomeMember) return;
            window.open(membershipUrl!, '_blank', 'noopener,noreferrer');
          }}
          className={`w-full text-left rounded-md border border-dashed p-3 transition-colors ${
            canBecomeMember
              ? 'border-amber-500/40 bg-background/40 hover:border-amber-500/70 hover:bg-amber-500/[0.06]'
              : 'border-border bg-muted/40 cursor-not-allowed opacity-60'
          }`}
        >
          <div className="flex items-center gap-2 font-medium text-sm">
            <ExternalLink className="h-4 w-4" />
            Not a member yet? Become one
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {canBecomeMember
              ? "Opens this organization's signup page in a new tab. Once you're approved, come back and finish your entry."
              : "This organization hasn't published a membership signup link yet. Contact them directly or pick one of the options above."}
          </p>
        </button>
      </div>
    </div>
  );
}
